import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BudgetItem } from './use-annual-budget';

interface CategoryTotal {
  category_id: string;
  month: number;
  total: number;
}

interface RecurrenceTotal {
  recurrence_id: string;
  month: number;
  total: number;
}

export function useBudgetSync(year: number, items: BudgetItem[]) {
  const [categoryTotals, setCategoryTotals] = useState<CategoryTotal[]>([]);
  const [recurrenceTotals, setRecurrenceTotals] = useState<RecurrenceTotal[]>([]);
  const [loading, setLoading] = useState(false);

  const syncActualValues = async () => {
    if (!items.length) return;

    try {
      setLoading(true);
      
      // Get all category IDs from budget items
      const categoryIds = [...new Set(items.map(i => i.category_id).filter(Boolean))] as string[];
      
      // Get all recurrence IDs from budget items that have linked recurrences
      const recurrenceIds = [...new Set(items.map(i => i.recurrence_id).filter(Boolean))] as string[];

      // Fetch category-level actuals
      if (categoryIds.length) {
        const { data, error } = await supabase.rpc('get_budget_actuals', {
          p_year: year,
          p_category_ids: categoryIds,
        });

        if (error) throw error;

        const totals: CategoryTotal[] = ((data as any[]) || []).map((row: any) => ({
          category_id: row.category_id,
          month: Number(row.month),
          total: Number(row.total),
        }));

        setCategoryTotals(totals);
      } else {
        setCategoryTotals([]);
      }

      // Fetch recurrence-level actuals (for linked items)
      if (recurrenceIds.length) {
        const { data, error } = await supabase.rpc('get_recurrence_actuals', {
          p_year: year,
          p_recurrence_ids: recurrenceIds,
        });

        if (error) throw error;

        const totals: RecurrenceTotal[] = ((data as any[]) || []).map((row: any) => ({
          recurrence_id: row.recurrence_id,
          month: Number(row.month),
          total: Number(row.total),
        }));

        setRecurrenceTotals(totals);
      } else {
        setRecurrenceTotals([]);
      }
    } catch (error) {
      console.error('Error syncing budget values:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Hybrid actual amount resolution:
   * - If the item has a linked recurrence, use recurrence-specific actuals (more precise)
   * - Otherwise, fall back to category-level aggregate
   */
  const getActualAmount = (categoryId: string | null, month: number, recurrenceId?: string | null): number => {
    // Prefer recurrence-specific actuals when available
    if (recurrenceId) {
      const found = recurrenceTotals.find(
        t => t.recurrence_id === recurrenceId && t.month === month
      );
      if (found) return found.total;
      // If recurrence has no data for this month, return 0 (not category fallback)
      return 0;
    }

    // Category-level fallback
    if (!categoryId) return 0;
    const found = categoryTotals.find(
      t => t.category_id === categoryId && t.month === month
    );
    return found?.total || 0;
  };

  useEffect(() => {
    syncActualValues();
  }, [year, items.length]);

  return {
    categoryTotals,
    recurrenceTotals,
    loading,
    getActualAmount,
    refresh: syncActualValues,
  };
}
